import { FC, Ref, useEffect, useMemo, useState } from "react";
import { Control, FieldValues, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  FormController,
  FormControllerCheckbox,
  MuiBox,
  MuiCheckbox,
  MuiGrid,
  MuiLink,
  MuiMultiSelect,
  MuiSelect,
  MuiTextField,
  MuiTypography,
  MultiOptionsDataType,
} from "../../../../../../../components";
import { useSiteCore } from "../../../../../../../hooks/useSiteCore";
import { activitiesAndDeliverablesSiteCoreKeys } from "../../../../../siteCore/SiteCoreKeys";

import { useGetCreateClientActivityDeliverables } from "../../../../../../../api/services/client/clientApis";
import LabelWithInfoIcon from "../../../../../../../components/labelWithInfoIcon/LabelWithInfoIcon";
import { ManageLocale } from "../../../../../../../components/manageLocale/ManageLocale";
import { MAX_LENGTH_100 } from "../../../../../../../helpers/constants";
import { KeyValue } from "../../../../../../../helpers/types/common";
import {
  setApiDataToDropDownOption,
  setApiDataToDropDownOptionForActivityType,
} from "../../../../../../../helpers/utils";
import { useMultiSelectARIALabels } from "../../../../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../../../../hooks/useNavigationSiteCore";
import { userPreferredLanguageSelector } from "../../../../../../../redux/slices/appState/appStateSelector";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../../redux/slices/appState/appStateSlice";
import { createClientSelector } from "../../../../../../../redux/slices/client/createClientSelector";
import { MdmSearchActivityResultRowValue } from "../../../../../../mdmActivitySearch/modelsAndSchemas/models";
import {
  CreateActivityGeneralFormKeys,
  CreateActivityGeneralProps,
} from "../../../modelsAndSchemas/models";

interface CreateActivityProps extends FieldValues {
  control: Control<CreateActivityGeneralProps>;
  pageId: string;
  deliveryRef: Ref<{ resetValues: () => void }>;
  isPendingActivity: boolean; // for loader
  children?: React.ReactNode;
  handleSaveManageLocal: (localeData: KeyValue) => void;
  manageLocaleTitle: string;
  activityTypeList: MdmSearchActivityResultRowValue[];
  manageLocaleInfoMessage?: string;
  participantStatusRef: Ref<{ resetValues: (values?: string[]) => void }>;
  modalityList?: MultiOptionsDataType[];
  modalityRef: Ref<{ resetValues: (values?: string[]) => void }>;
}

export const CreateActivityGeneral: FC<CreateActivityProps> = ({
  control,
  pageId,
  deliveryRef,
  isPendingActivity,
  children,
  manageLocaleTitle,
  manageLocaleInfoMessage,
  handleSaveManageLocal,
  activityTypeList,
  participantStatusRef,
  modalityRef,
  modalityList,
}) => {
  const dispatch = useDispatch();
  const gridSize = { md: 4, sm: 6, xs: 12 };
  const {
    commonLabels,
    fetchLabel,
    fetchDropdownValues,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { id: clientId } = useSelector(createClientSelector);
  const userSelectedLanguage = useSelector(userPreferredLanguageSelector);
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const {
    data: activityDeliverables,
    isLoading: activityDeliverablesPending,
    isError: isActivityDeliverablesError,
    isSuccess: isActivityDeliverablesSuccess,
  } = useGetCreateClientActivityDeliverables(clientId);

  const [showManageLocale, setShowManageLocale] = useState(false);
  const handleShowManageLocale = () => {
    setShowManageLocale(true);
  };

  const handleCloseShowManageLocale = () => {
    setShowManageLocale(false);
  };

  const topicNames = useWatch({
    name: CreateActivityGeneralFormKeys.topic,
    control,
  });

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: activityDeliverablesPending || isPendingActivity,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [activityDeliverablesPending, isPendingActivity]);

  useEffect(() => {
    if (isActivityDeliverablesError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isActivityDeliverablesError]);

  const activityTypesDropdownOption = useMemo(() => {
    if (!activityTypeList.length) {
      return [];
    }
    return setApiDataToDropDownOptionForActivityType(activityTypeList);
  }, [activityTypeList]);

  return (
    <>
      <MuiBox data-testid="activity-tab-general">
        <MuiGrid container className="activity-tab-general">
          <MuiGrid size={gridSize}>
            <FormController
              control={control}
              name={CreateActivityGeneralFormKeys.candidateStatuses}
              ref={participantStatusRef}
            >
              <MuiMultiSelect
                label={fetchLabel(
                  activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
                  activitiesAndDeliverablesSiteCoreKeys.participantStatus,
                )}
                options={fetchDropdownValues(
                  activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
                  activitiesAndDeliverablesSiteCoreKeys.participantStatusList,
                )}
                showSearch
                showChip
                isMandatory
                showSelectAllOption
                chipId={`${pageId}-${CreateActivityGeneralFormKeys.candidateStatuses}-chip`}
                dialogTitle={fetchLabel(
                  activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
                  activitiesAndDeliverablesSiteCoreKeys.participantStatus,
                )}
                id={`${pageId}-${CreateActivityGeneralFormKeys.candidateStatuses}`}
                selectPlaceHolder={commonLabels.selectPlaceHolder}
                searchPlaceHolder={commonLabels.search}
                selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                doneText={commonLabels.done}
                allARIAProps={multiSelectARIALabels}
                data-testid="activity-general-participant-status"
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSize}>
            <FormController
              control={control}
              name={CreateActivityGeneralFormKeys.activityType}
            >
              <MuiSelect
                label={fetchLabel(
                  activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
                  activitiesAndDeliverablesSiteCoreKeys.activityType,
                )}
                options={activityTypesDropdownOption}
                visibleDefaultSelectOption
                showSearch
                id={`${pageId}-${CreateActivityGeneralFormKeys.activityType}`}
                isMandatory
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
                data-testid="activity-general-activity-type"
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSize}>
            <FormController
              control={control}
              name={CreateActivityGeneralFormKeys.modality}
              ref={modalityRef}
            >
              <MuiMultiSelect
                label={fetchLabel(
                  activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
                  activitiesAndDeliverablesSiteCoreKeys.modality,
                )}
                showSearch
                showChip
                showSelectAllOption
                options={modalityList ?? []}
                id={`${pageId}-${activitiesAndDeliverablesSiteCoreKeys.modality}`}
                chipId={`${pageId}-${activitiesAndDeliverablesSiteCoreKeys.modality}-chip`}
                value=""
                dialogTitle={fetchLabel(
                  activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
                  activitiesAndDeliverablesSiteCoreKeys.modality,
                )}
                selectPlaceHolder={commonLabels.selectPlaceHolder}
                searchPlaceHolder={commonLabels.search}
                selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                doneText={commonLabels.done}
                allARIAProps={multiSelectARIALabels}
                data-testid="activity-general-modality"
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize}>
            <MuiBox className="topic-name">
              <FormController
                control={control}
                name={`${CreateActivityGeneralFormKeys.topic}.${userSelectedLanguage}`}
              >
                <MuiTextField
                  label={fetchLabel(
                    activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
                    activitiesAndDeliverablesSiteCoreKeys.topic,
                  )}
                  id={`${pageId}-${CreateActivityGeneralFormKeys.topic}`}
                  isMandatory
                  placeholder={commonLabels.inputPlaceHolder}
                  maxLength={MAX_LENGTH_100}
                  showCloseIcon
                  requiredARIALabel={commonARIALabel.required}
                  data-testid="activity-general-topic"
                  tooltipMessage={
                    commonValidationMessages.fieldSpecialCharactersTooltipMessage
                  }
                  tooltipHeaderTitle={
                    commonValidationMessages.allowedSpecialCharactersTitle
                  }
                />
              </FormController>
              <MuiLink
                className="topic-manage-locale-link"
                id={`${pageId}-topic-locale`}
                data-testid={`${pageId}-topic-locale`}
                onClick={handleShowManageLocale}
              >
                {commonLabels.locale}
              </MuiLink>
            </MuiBox>
          </MuiGrid>
          <MuiGrid size={gridSize}>
            <FormController
              control={control}
              name={CreateActivityGeneralFormKeys.deliverables}
              ref={deliveryRef}
            >
              <MuiMultiSelect
                label={fetchLabel(
                  activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
                  activitiesAndDeliverablesSiteCoreKeys.deliverables,
                )}
                options={setApiDataToDropDownOption(activityDeliverables)}
                showSearch
                showChip
                showSelectAllOption
                id={`${pageId}-${CreateActivityGeneralFormKeys.deliverables}`}
                chipId={`${pageId}-${CreateActivityGeneralFormKeys.deliverables}-chip`}
                dialogTitle={fetchLabel(
                  activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
                  activitiesAndDeliverablesSiteCoreKeys.selectedDeliverables,
                )}
                selectPlaceHolder={commonLabels.selectPlaceHolder}
                searchPlaceHolder={commonLabels.search}
                selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                doneText={commonLabels.done}
                allARIAProps={multiSelectARIALabels}
                data-testid="activity-general-deliverables"
              />
            </FormController>
            {!activityDeliverables?.length && isActivityDeliverablesSuccess && (
              <LabelWithInfoIcon
                label={fetchLabel(
                  activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
                  activitiesAndDeliverablesSiteCoreKeys.noDeliverablesAvailable,
                )}
                tooltipMessage={fetchLabel(
                  activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
                  activitiesAndDeliverablesSiteCoreKeys.noTaggedDeliverablesTooltip,
                )}
              />
            )}
          </MuiGrid>

          <MuiGrid size={gridSize}>
            <FormController
              control={control}
              name={CreateActivityGeneralFormKeys.activityStatus}
            >
              <MuiSelect
                label={fetchLabel(
                  activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
                  activitiesAndDeliverablesSiteCoreKeys.activityStatus,
                )}
                options={fetchDropdownValues(
                  activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
                  activitiesAndDeliverablesSiteCoreKeys.activityStatusList,
                )}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${CreateActivityGeneralFormKeys.activityStatus}`}
                isMandatory
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
                data-testid="activity-general-activity-status"
              />
            </FormController>
          </MuiGrid>
        </MuiGrid>
        <MuiBox>
          <MuiTypography
            variant="h2Bold"
            className="select-applicable default-activity-value"
            component="h2"
          >
            {fetchLabel(
              activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
              activitiesAndDeliverablesSiteCoreKeys.defaultActivityValues,
            )}
          </MuiTypography>
        </MuiBox>
        {children}
        <MuiBox>
          <MuiTypography
            id="id-group-label"
            variant="h2Bold"
            className="select-applicable"
            component="h2"
          >
            {fetchLabel(
              activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
              activitiesAndDeliverablesSiteCoreKeys.selectApplicable,
            )}
          </MuiTypography>
        </MuiBox>
        <MuiBox role="group" aria-labelledby="id-group-label">
          <MuiGrid role="list" container className="checkbox-container">
            <MuiGrid size={gridSize} role="listitem">
              <FormControllerCheckbox
                control={control}
                name={CreateActivityGeneralFormKeys.triggerBilling}
              >
                <MuiCheckbox
                  checkboxLabel={fetchLabel(
                    activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
                    activitiesAndDeliverablesSiteCoreKeys.triggerBilling,
                  )}
                  id={`${pageId}-${CreateActivityGeneralFormKeys.triggerBilling}`}
                  data-testid={`${pageId}-${CreateActivityGeneralFormKeys.triggerBilling}`}
                />
              </FormControllerCheckbox>
            </MuiGrid>
            <MuiGrid size={gridSize} role="listitem">
              <FormControllerCheckbox
                control={control}
                name={CreateActivityGeneralFormKeys.lastContactDate}
              >
                <MuiCheckbox
                  checkboxLabel={fetchLabel(
                    activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
                    activitiesAndDeliverablesSiteCoreKeys.countsTowardsLastContactDate,
                  )}
                  id={`${pageId}-${CreateActivityGeneralFormKeys.lastContactDate}`}
                />
              </FormControllerCheckbox>
            </MuiGrid>
            <MuiGrid size={gridSize} role="listitem">
              <FormControllerCheckbox
                control={control}
                name={CreateActivityGeneralFormKeys.triggerTouch}
              >
                <MuiCheckbox
                  checkboxLabel={fetchLabel(
                    activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
                    activitiesAndDeliverablesSiteCoreKeys.triggerTouchPointSurvey,
                  )}
                  id={`${pageId}-${CreateActivityGeneralFormKeys.triggerTouch}`}
                  data-testid="activity-general-trigger-billing"
                />
              </FormControllerCheckbox>
            </MuiGrid>
          </MuiGrid>
        </MuiBox>
      </MuiBox>
      {showManageLocale && (
        <ManageLocale
          show={showManageLocale}
          handleCloseModal={handleCloseShowManageLocale}
          localeLanguages={topicNames}
          onSaveManageLocalCallback={handleSaveManageLocal}
          title={manageLocaleTitle}
          bannerInfoMessage={manageLocaleInfoMessage}
        />
      )}
    </>
  );
};
